import { isNull, isSome } from '@mbfab/shared'
import { X } from 'lucide-react'
import type { ChangeEvent, ComponentProps, Ref, RefCallback } from 'react'
import { useCallback, useRef, useState } from 'react'

// Ant Design の allowClear 相当のクリアボタンを FormInput / CompactFormInput / Input で共有する部品。
//
// クリア時は DOM の value を直接書き換えるだけでは React の onChange が発火しない
// （React が value プロパティの setter をラップして変更を検知しているため）。
// ネイティブの HTMLInputElement.prototype.value の setter 経由で値をセットしてから
// input イベントを dispatch することで、制御・非制御どちらの呼び出し元の onChange も
// 通常のユーザー入力と同じ経路で呼び出す（React で広く使われる定番の回避策）
export function dispatchNativeInputChange(input: HTMLInputElement, value: string) {
  const descriptor = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')
  descriptor?.set?.call(input, value)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}

function hasContent(value: ComponentProps<'input'>['value']): boolean {
  return isSome(value) && String(value).length > 0
}

// 呼び出し元が渡した ref（forwardedRef）とクリア処理用の内部 ref を両方生かすための合成。
// React 19 は `ref` を通常の prop として渡せるため、フックの戻り値の ref を
// 呼び出し元の `{...props}` より後ろに置くだけでは呼び出し元の ref が上書きされたままになる
// （props に ref が含まれる場合、展開順で最後に勝つため）。合成した1つの ref を返すことで、
// 呼び出し元が spread の後に置いても・前に置いても両方の ref に DOM ノードが渡るようにする
export function mergeRefs<T>(...refs: Array<Ref<T> | null | undefined>): RefCallback<T> {
  return (node) => {
    for (const ref of refs) {
      if (typeof ref === 'function') {
        ref(node)
      } else if (isSome(ref)) {
        ref.current = node
      }
    }
  }
}

type UseClearableInputArgs = Pick<ComponentProps<'input'>, 'value' | 'defaultValue' | 'onChange' | 'ref'>

/**
 * クリアボタンの表示状態（値が空かどうか）とクリア処理を、制御・非制御どちらの
 * 呼び出し元でも動く形でまとめたフック。呼び出し元は返ってきた ref を入力欄に、
 * handleChange を onChange に差し替えて使う
 */
export function useClearableInput({ value, defaultValue, onChange, ref: forwardedRef }: UseClearableInputArgs) {
  // クリア処理（handleClear）は DOM ノードに直接アクセスする必要があるため、
  // 呼び出し元から渡された ref とは別に、常に生きている内部 ref を持つ
  const internalRef = useRef<HTMLInputElement>(null)
  const [uncontrolledHasValue, setUncontrolledHasValue] = useState(() => hasContent(defaultValue))
  const hasValue = isSome(value) ? hasContent(value) : uncontrolledHasValue

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUncontrolledHasValue(hasContent(event.target.value))
    onChange?.(event)
  }

  const handleClear = () => {
    const input = internalRef.current
    if (isNull(input)) {
      return
    }
    dispatchNativeInputChange(input, '')
    input.focus()
  }

  const ref = useCallback(
    (node: HTMLInputElement | null) => {
      mergeRefs(internalRef, forwardedRef)(node)
    },
    [forwardedRef],
  )

  return { ref, hasValue, handleChange, handleClear }
}

/**
 * 入力欄右端に絶対配置するクリアボタン。position: absolute のため、
 * 表示・非表示を切り替えても入力欄自体の幅・高さには影響しない
 * （呼び出し元は clearable 時に常に右パディングを確保しておくこと）
 */
export function ClearButton({ onClear, testId }: { onClear: () => void; testId?: string }) {
  return (
    <button
      type="button"
      aria-label="クリア"
      data-testid={testId}
      onClick={onClear}
      className="absolute top-1/2 right-2.5 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      <X className="size-3.5" />
    </button>
  )
}
